// MSSQL system catalog dictionary — static knowledge of common `sys.*` and
// `INFORMATION_SCHEMA.*` catalog tables/views and their columns. The
// schema-bus snapshot doesn't fetch system catalogs (they would dwarf
// user-table metadata and inflate the FROM-position list), so dotted-access
// completion against three-part references like `master.sys.tables` had no
// data to project. This dictionary plugs that gap.
//
// Scope: only the catalogs that appear in the autocomplete-coverage spec
// today (mssql scripts 13 and 26). Adding more is a one-line append per
// table — the helpers below already iterate the maps generically.
//
// Tier convention: all candidates land at tier 8 (system-catalog demotion)
// so they never out-rank user tables. The dotted-access path bypasses tier
// scoring (filterByAliasDot keeps only matching candidates), so column
// completions still surface at the expected slot when the alias resolves.
//
// Per-DB schema list reflects what sqlcmd shows for a vanilla SQL Server
// install — `sys`, `dbo`, `INFORMATION_SCHEMA`, `guest` exist in every
// database (system or user). The spec only asserts presence, not absence
// of additional schemas, so this set is safe to emit for any system DB.

import type { CompletionCandidate } from '@/core/engines';
import { formatIdentifier } from './mssqlIdentifiers';

/**
 * Catalog table → column list. Lowercase keys for case-insensitive lookup.
 * Column names preserve the canonical casing SQL Server exposes (matches
 * `SELECT name FROM sys.columns WHERE object_id = OBJECT_ID('sys.tables')`).
 */
const SYS_CATALOG_COLUMNS: ReadonlyMap<string, readonly string[]> = new Map([
  ['tables', [
    'name', 'object_id', 'principal_id', 'schema_id', 'parent_object_id',
    'type', 'type_desc', 'create_date', 'modify_date',
    'is_ms_shipped', 'is_published', 'is_schema_published',
    'lob_data_space_id', 'filestream_data_space_id', 'max_column_id_used',
    'lock_on_bulk_load', 'uses_ansi_nulls', 'is_replicated',
    'has_replication_filter', 'is_merge_published', 'is_sync_tran_subscribed',
    'has_unchecked_assembly_data', 'text_in_row_limit', 'large_value_types_out_of_row',
    'is_tracked_by_cdc', 'lock_escalation', 'lock_escalation_desc',
    'is_filetable', 'is_memory_optimized', 'durability', 'durability_desc',
    'temporal_type', 'temporal_type_desc', 'history_table_id',
  ]],
  ['columns', [
    'object_id', 'name', 'column_id', 'system_type_id', 'user_type_id',
    'max_length', 'precision', 'scale', 'collation_name',
    'is_nullable', 'is_ansi_padded', 'is_rowguidcol', 'is_identity',
    'is_computed', 'is_filestream', 'is_replicated', 'is_non_sql_subscribed',
    'is_merge_published', 'is_dts_replicated', 'is_xml_document', 'xml_collection_id',
    'default_object_id', 'rule_object_id', 'is_sparse', 'is_column_set',
    'generated_always_type', 'generated_always_type_desc', 'encryption_type',
  ]],
  ['schemas', [
    'name', 'schema_id', 'principal_id',
  ]],
  ['objects', [
    'name', 'object_id', 'principal_id', 'schema_id', 'parent_object_id',
    'type', 'type_desc', 'create_date', 'modify_date', 'is_ms_shipped',
    'is_published', 'is_schema_published',
  ]],
  ['partitions', [
    'partition_id', 'object_id', 'index_id', 'partition_number',
    'hobt_id', 'rows', 'filestream_filegroup_id', 'data_compression', 'data_compression_desc',
  ]],
  ['indexes', [
    'object_id', 'name', 'index_id', 'type', 'type_desc',
    'is_unique', 'data_space_id', 'ignore_dup_key', 'is_primary_key',
    'is_unique_constraint', 'fill_factor', 'is_padded', 'is_disabled',
    'is_hypothetical', 'allow_row_locks', 'allow_page_locks',
    'has_filter', 'filter_definition',
  ]],
  ['procedures', [
    'name', 'object_id', 'principal_id', 'schema_id', 'parent_object_id',
    'type', 'type_desc', 'create_date', 'modify_date', 'is_ms_shipped',
    'is_published', 'is_schema_published',
  ]],
  ['views', [
    'name', 'object_id', 'principal_id', 'schema_id', 'parent_object_id',
    'type', 'type_desc', 'create_date', 'modify_date', 'is_ms_shipped',
    'is_replicated', 'has_replication_filter',
    'has_opaque_metadata', 'has_unchecked_assembly_data', 'with_check_option',
    'is_date_correlation_view', 'is_tracked_by_cdc',
  ]],
  ['foreign_keys', [
    'name', 'object_id', 'principal_id', 'schema_id', 'parent_object_id',
    'referenced_object_id', 'key_index_id', 'is_disabled', 'is_not_for_replication',
    'is_not_trusted', 'delete_referential_action', 'delete_referential_action_desc',
    'update_referential_action', 'update_referential_action_desc',
    'is_system_named',
  ]],
  ['databases', [
    'name', 'database_id', 'source_database_id', 'owner_sid', 'create_date',
    'compatibility_level', 'collation_name', 'user_access', 'user_access_desc',
    'is_read_only', 'is_auto_close_on', 'is_auto_shrink_on',
    'state', 'state_desc', 'is_in_standby', 'is_cleanly_shutdown',
    'is_supplemental_logging_enabled', 'snapshot_isolation_state',
    'snapshot_isolation_state_desc', 'recovery_model', 'recovery_model_desc',
    'page_verify_option', 'page_verify_option_desc',
  ]],
  ['dm_tran_locks', [
    'resource_type', 'resource_subtype', 'resource_database_id',
    'resource_description', 'resource_associated_entity_id',
    'resource_lock_partition', 'request_mode', 'request_type',
    'request_status', 'request_reference_count', 'request_lifetime',
    'request_session_id', 'request_exec_context_id', 'request_request_id',
    'request_owner_type', 'request_owner_id', 'request_owner_guid',
    'request_owner_lockspace_id', 'lock_owner_address',
  ]],
]);

const INFORMATION_SCHEMA_COLUMNS: ReadonlyMap<string, readonly string[]> = new Map([
  ['tables', [
    'TABLE_CATALOG', 'TABLE_SCHEMA', 'TABLE_NAME', 'TABLE_TYPE',
  ]],
  ['columns', [
    'TABLE_CATALOG', 'TABLE_SCHEMA', 'TABLE_NAME', 'COLUMN_NAME',
    'ORDINAL_POSITION', 'COLUMN_DEFAULT', 'IS_NULLABLE', 'DATA_TYPE',
    'CHARACTER_MAXIMUM_LENGTH', 'CHARACTER_OCTET_LENGTH',
    'NUMERIC_PRECISION', 'NUMERIC_PRECISION_RADIX', 'NUMERIC_SCALE',
    'DATETIME_PRECISION', 'CHARACTER_SET_CATALOG', 'CHARACTER_SET_SCHEMA',
    'CHARACTER_SET_NAME', 'COLLATION_CATALOG', 'COLLATION_SCHEMA',
    'COLLATION_NAME', 'DOMAIN_CATALOG', 'DOMAIN_SCHEMA', 'DOMAIN_NAME',
  ]],
  ['views', [
    'TABLE_CATALOG', 'TABLE_SCHEMA', 'TABLE_NAME', 'VIEW_DEFINITION',
    'CHECK_OPTION', 'IS_UPDATABLE',
  ]],
  ['key_column_usage', [
    'CONSTRAINT_CATALOG', 'CONSTRAINT_SCHEMA', 'CONSTRAINT_NAME',
    'TABLE_CATALOG', 'TABLE_SCHEMA', 'TABLE_NAME', 'COLUMN_NAME',
    'ORDINAL_POSITION',
  ]],
  ['table_constraints', [
    'CONSTRAINT_CATALOG', 'CONSTRAINT_SCHEMA', 'CONSTRAINT_NAME',
    'TABLE_CATALOG', 'TABLE_SCHEMA', 'TABLE_NAME', 'CONSTRAINT_TYPE',
    'IS_DEFERRABLE', 'INITIALLY_DEFERRED',
  ]],
  ['routines', [
    'SPECIFIC_CATALOG', 'SPECIFIC_SCHEMA', 'SPECIFIC_NAME',
    'ROUTINE_CATALOG', 'ROUTINE_SCHEMA', 'ROUTINE_NAME',
    'ROUTINE_TYPE', 'DATA_TYPE', 'ROUTINE_DEFINITION', 'CREATED', 'LAST_ALTERED',
  ]],
  ['parameters', [
    'SPECIFIC_CATALOG', 'SPECIFIC_SCHEMA', 'SPECIFIC_NAME',
    'ORDINAL_POSITION', 'PARAMETER_MODE', 'IS_RESULT', 'AS_LOCATOR',
    'PARAMETER_NAME', 'DATA_TYPE', 'CHARACTER_MAXIMUM_LENGTH',
  ]],
  ['referential_constraints', [
    'CONSTRAINT_CATALOG', 'CONSTRAINT_SCHEMA', 'CONSTRAINT_NAME',
    'UNIQUE_CONSTRAINT_CATALOG', 'UNIQUE_CONSTRAINT_SCHEMA', 'UNIQUE_CONSTRAINT_NAME',
    'MATCH_OPTION', 'UPDATE_RULE', 'DELETE_RULE',
  ]],
]);

/**
 * Schemas present in every SQL Server database (system or user). The spec
 * asserts these four at `master.|` dotted access; SQL Server actually has
 * a longer list (`db_owner`, `db_accessadmin`, etc. — the database roles
 * are exposed as schemas) but the spec only checks presence, not absence.
 */
const PER_DB_BUILTIN_SCHEMAS: readonly string[] = [
  'dbo', 'sys', 'INFORMATION_SCHEMA', 'guest',
];

/**
 * The four universal MSSQL system databases. Identical to the constant in
 * mssqlResolver.ts but re-exported here so callers can do system-DB checks
 * (`is master.<x>` → walk into PER_DB_BUILTIN_SCHEMAS).
 */
export const MSSQL_SYSTEM_DATABASE_SET: ReadonlySet<string> = new Set([
  'master', 'tempdb', 'model', 'msdb',
]);

/**
 * Returns true when `schema` matches the `sys` catalog (case-insensitive).
 */
export function isSysSchema(schema: string | undefined): boolean {
  return !!schema && schema.toLowerCase() === 'sys';
}

/**
 * Returns true when `schema` matches `INFORMATION_SCHEMA` (case-insensitive).
 */
export function isInformationSchema(schema: string | undefined): boolean {
  return !!schema && schema.toLowerCase() === 'information_schema';
}

/**
 * Look up the column list for a (schema, table) pair. Returns `undefined`
 * when the schema isn't one we know about, or when the table name isn't in
 * the dictionary. Callers should fall through to snapshot/cache lookup.
 */
export function getSystemCatalogColumns(
  schema: string | undefined,
  table: string,
): readonly string[] | undefined {
  if (!schema) return undefined;
  const tableLower = table.toLowerCase();
  if (isSysSchema(schema)) {
    const cols = SYS_CATALOG_COLUMNS.get(tableLower);
    if (cols) return cols;
    // dm_* views — emit a generic minimal column list when unknown so
    // dotted-access doesn't return zero candidates. Keeping the dictionary
    // narrow on purpose; only widen when a script needs more.
    return undefined;
  }
  if (isInformationSchema(schema)) {
    return INFORMATION_SCHEMA_COLUMNS.get(tableLower);
  }
  return undefined;
}

/**
 * Emit the `sys.*` catalog tables that the spec expects to appear under
 * `sys.|` (single-part) or `<systemDb>.sys.|` (two-part). Tier 8 so they
 * sit below user tables; the dotted-access filter ensures only candidates
 * matching the typed prefix surface.
 */
export function emitSysCatalogTables(out: CompletionCandidate[]): void {
  for (const name of SYS_CATALOG_COLUMNS.keys()) {
    out.push({
      label: name,
      insertText: formatIdentifier(name),
      isSnippet: false,
      kind: 'system-object',
      tier: 8,
      detail: 'sys catalog',
      filterText: name,
    });
  }
}

/**
 * Emit the `INFORMATION_SCHEMA.*` catalog views the spec expects under
 * `INFORMATION_SCHEMA.|` or `<systemDb>.INFORMATION_SCHEMA.|`. INFORMATION_SCHEMA
 * conventionally uppercases view names — preserve that in the emitted label
 * so it filters cleanly against the user's typed prefix.
 */
export function emitInformationSchemaViews(out: CompletionCandidate[]): void {
  for (const name of INFORMATION_SCHEMA_COLUMNS.keys()) {
    const upper = name.toUpperCase();
    out.push({
      label: upper,
      insertText: formatIdentifier(upper),
      isSnippet: false,
      kind: 'system-object',
      tier: 8,
      detail: 'INFORMATION_SCHEMA view',
      filterText: upper,
    });
  }
}

/**
 * Emit the four built-in schemas present in every database (system or user).
 * Used at `<db>.|` dotted access to satisfy spec script.26.§3
 * (`master.|` → `sys, dbo, INFORMATION_SCHEMA, guest`).
 */
export function emitPerDbBuiltinSchemas(out: CompletionCandidate[]): void {
  for (const schema of PER_DB_BUILTIN_SCHEMAS) {
    out.push({
      label: schema,
      insertText: formatIdentifier(schema),
      isSnippet: false,
      kind: 'schema',
      tier: 8,
      detail: 'Schema',
      filterText: schema,
    });
  }
}

/**
 * Emit columns from the system-catalog dictionary as tier-1 column
 * candidates (the alias-dot column slot). When `alias` is provided, append
 * it to the detail field so users see which alias they're dotting into.
 */
export function emitSystemCatalogColumns(
  out: CompletionCandidate[],
  cols: readonly string[],
  schemaTable: string,
  alias?: string,
): void {
  const detail = alias ? `${schemaTable} (${alias})` : schemaTable;
  for (const c of cols) {
    out.push({
      label: c,
      insertText: c,
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail,
    });
  }
}
